import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaStar } from 'react-icons/fa';
import useAuth from '../hooks/useAuth';
import { API_BASE } from '../config/api';
import RefundForm from '../components/RefundForm/RefundForm';
import RefundNotification from '../components/RefundNotification/RefundNotification';
import { formatMaterialDisplayLabel } from '../utils/materialLabels';
import './OrderTrackingPage.css';
import axios from 'axios';

const OrderTrackingPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState('');
  const [refundData, setRefundData] = useState(null);
  const [showRefundNotification, setShowRefundNotification] = useState(false);
  const [userReviews, setUserReviews] = useState([]); // New: user's reviews for this order
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilters, setStatusFilters] = useState({
    onTheWay: false,
    delivered: false,
    cancelled: false,
  });
  const [timeFilters, setTimeFilters] = useState({
    last30: false,
    y2024: false,
    y2023: false,
    older: false,
  });
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrdersOrOrder = async () => {
      try {
        setLoading(true);
        setError('');
        if (id) {
          // Fetch specific order by ID
          const response = await fetch(`${API_BASE}/${id}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (!response.ok) {
            if (response.status === 404) throw new Error('Order not found');
            throw new Error('Failed to fetch order details');
          }
          const data = await response.json();
          setOrder(data);
          
          // Check if there's a refund for this order
          if (data.orderStatus === 'Cancelled' && data.paymentMethod === 'online' && data.isPaid) {
            await checkRefundStatus(data._id);
          }
        } else {
          // Fetch all orders and filter undelivered
          setOrder(null); // Clear any previous order detail
          const response = await fetch(API_BASE, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
          if (!response.ok) throw new Error('Failed to fetch orders');
          const data = await response.json();
          // Filter out failed payment orders
          const validOrders = data.filter(
            order => order.orderStatus !== 'Payment Failed' && order.orderStatus !== 'Payment Not Completed'
          );
          setOrders(validOrders);
        }
      } catch (error) {
        setError(error.message || 'An error occurred while fetching your order details');
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchOrdersOrOrder();
      if (id && user) {
        // Fetch user's reviews for this order
        const fetchUserReviews = async () => {
          try {
            const res = await axios.get(`${API_BASE}/reviews/order/${id}/user`, { withCredentials: true });
            setUserReviews(res.data.reviews || []);
          } catch (err) {
            setUserReviews([]);
          }
        };
        fetchUserReviews();
      }
    } else {
      setLoading(false);
      setError('Please log in to view your orders');
    }
  }, [id, user]);

  const checkRefundStatus = async (orderId) => {
    try {
      const response = await fetch(`${API_BASE}/refund/requests`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const refundForThisOrder = data.refunds?.find(refund => refund.order._id === orderId);
        if (refundForThisOrder) {
          setRefundData(refundForThisOrder);
          // Show notification if status is not pending
          if (refundForThisOrder.refundStatus !== 'Pending') {
            setShowRefundNotification(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking refund status:', error);
    }
  };

  // Helper to check if a product in this order is already reviewed by the user
  const isProductReviewed = (productId) => {
    return userReviews.some(r => r.product === productId);
  };

  // Handle cancel order
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      setCancelError('Please provide a reason for cancellation');
      return;
    }

    setCancelLoading(true);
    setCancelError('');

    try {
      const response = await fetch(`${API_BASE}/${order._id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: cancelReason })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel order');
      }

      const updatedOrder = await response.json();
      setOrder(updatedOrder);
      setShowCancelModal(false);
      setCancelReason('');
      setCancelError('');
    } catch (error) {
      setCancelError(error.message || 'An error occurred while cancelling the order');
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusStep = () => {
    if (!order) return 1;
    
    const { orderStatus } = order;
    
    switch (orderStatus) {
      case 'Processing':
        return 1;
      case 'Shipped':
        return 2;
      case 'Out for Delivery':
        return 3;
      case 'Delivered':
        return 4;
      case 'Cancelled':
        return 5;
      default:
        return 1;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getProgressWidth = () => {
    if (!order) return '0%';
    if (order.orderStatus === 'Cancelled') return '25%';
    if (order.orderStatus === 'Delivered') return '100%';
    return `${((statusStep - 1) / 3) * 100}%`;
  };

  if (!id) {
    if (loading) {
      return (
        <div className="order-tracking-container">
          <div className="loading-spinner"></div>
          <p>Loading your orders...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="order-tracking-container">
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <div className="order-actions">
              <Link to="/" className="action-button continue-shopping">Continue Shopping</Link>
              <Link to="/account" className="action-button back-to-account">Back to Account</Link>
            </div>
          </div>
        </div>
      );
    }
    if (orders.length > 0) {
      const validOrders = orders.filter(
        o => o.orderStatus !== 'Payment Failed' && o.orderStatus !== 'Payment Not Completed'
      );
      const hasAnyStatusFilter = statusFilters.onTheWay || statusFilters.delivered || statusFilters.cancelled;
      const hasAnyTimeFilter = timeFilters.last30 || timeFilters.y2024 || timeFilters.y2023 || timeFilters.older;
      const hasSearchQuery = orderSearch.trim().length > 0;
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const filteredOrders = validOrders.filter(order => {
        if (hasAnyStatusFilter) {
          const status = order.orderStatus;
          const statusMatch =
            (statusFilters.onTheWay && (status === 'Processing' || status === 'Shipped' || status === 'Out for Delivery')) ||
            (statusFilters.delivered && status === 'Delivered') ||
            (statusFilters.cancelled && status === 'Cancelled');
          if (!statusMatch) return false;
        }

        if (hasAnyTimeFilter) {
          const d = new Date(order.createdAt);
          const timeMatch =
            (timeFilters.last30 && d >= thirtyDaysAgo) ||
            (timeFilters.y2024 && d.getFullYear() === 2024) ||
            (timeFilters.y2023 && d.getFullYear() === 2023) ||
            (timeFilters.older && d.getFullYear() < 2023);
          if (!timeMatch) return false;
        }

        if (hasSearchQuery) {
          const q = orderSearch.toLowerCase();
          const matches = order.orderItems.some(
            item => (item.title || '').toLowerCase().includes(q) || (item.material || '').toLowerCase().includes(q)
          );
          if (!matches) return false;
        }
        return true;
      });

      if (validOrders.length === 0) {
        return (
          <div className="order-tracking-container my-orders-layout">
            <div className="error-message">
              <h2>No Orders</h2>
              <p>You don't have any orders yet.</p>
              <div className="order-actions">
                <Link to="/" className="action-button continue-shopping">Continue Shopping</Link>
                <Link to="/account" className="action-button back-to-account">Back to Account</Link>
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className="my-orders-page">
          <nav className="my-orders-breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/account">My Account</Link>
            <span>›</span>
            <span>My Orders</span>
          </nav>

          <div className="my-orders-layout">
            <aside className="my-orders-filters">
              <h3 className="filters-title">Filters</h3>
              <div className="filter-section">
                <h4 className="filter-heading">ORDER STATUS</h4>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={statusFilters.onTheWay}
                    onChange={e => setStatusFilters(f => ({ ...f, onTheWay: e.target.checked }))}
                  />
                  <span>On the way</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={statusFilters.delivered}
                    onChange={e => setStatusFilters(f => ({ ...f, delivered: e.target.checked }))}
                  />
                  <span>Delivered</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={statusFilters.cancelled}
                    onChange={e => setStatusFilters(f => ({ ...f, cancelled: e.target.checked }))}
                  />
                  <span>Cancelled</span>
                </label>
              </div>
              <div className="filter-section">
                <h4 className="filter-heading">ORDER TIME</h4>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={timeFilters.last30}
                    onChange={e => setTimeFilters(f => ({ ...f, last30: e.target.checked }))}
                  />
                  <span>Last 30 days</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={timeFilters.y2024}
                    onChange={e => setTimeFilters(f => ({ ...f, y2024: e.target.checked }))}
                  />
                  <span>2024</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={timeFilters.y2023}
                    onChange={e => setTimeFilters(f => ({ ...f, y2023: e.target.checked }))}
                  />
                  <span>2023</span>
                </label>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={timeFilters.older}
                    onChange={e => setTimeFilters(f => ({ ...f, older: e.target.checked }))}
                  />
                  <span>Older</span>
                </label>
              </div>
            </aside>

            <main className="my-orders-main">
              <div className="my-orders-search-row">
                <input
                  type="text"
                  className="my-orders-search-input"
                  placeholder="Search your orders here"
                  value={orderSearch}
                  onChange={e => setOrderSearch(e.target.value)}
                />
                <button type="button" className="my-orders-search-btn">
                  <FaSearch /> Search Orders
                </button>
              </div>

              <div className="my-orders-list">
                {filteredOrders.length === 0 ? (
                  <p className="my-orders-empty">No orders match your filters.</p>
                ) : filteredOrders.flatMap(order =>
                  order.orderItems.map((item, idx) => (
                    <div
                      key={`${order._id}-${idx}`}
                      className="my-order-card"
                      onClick={() => navigate(`/order/${order._id}`)}
                    >
                      <div className="my-order-product">
                        <img src={item.image} alt={item.title} />
                        <div className="my-order-product-info">
                          <h4>{item.title}</h4>
                          <p className="my-order-product-meta">
                            {formatMaterialDisplayLabel(item.material) || 'Jewelry'}
                          </p>
                          {item.selectedSize ? <p className="my-order-product-meta">Size: {item.selectedSize}</p> : null}
                        </div>
                      </div>
                      <div className="my-order-price">
                        ₹{(item.price * item.quantity).toFixed(0)}
                      </div>
                      <div className="my-order-status-col">
                        {order.orderStatus === 'Delivered' ? (
                          <>
                            <div className="my-order-status-delivered">
                              <span className="status-dot delivered"></span>
                              Delivered on {formatDate(order.deliveredAt)}
                            </div>
                            <p className="my-order-status-msg">Your item has been delivered</p>
                            <button
                              type="button"
                              className="my-order-rate-link"
                              onClick={e => { e.stopPropagation(); navigate(`/order/${order._id}`); }}
                            >
                              <FaStar /> Rate & Review Product
                            </button>
                          </>
                        ) : order.orderStatus === 'Cancelled' ? (
                          <div className="my-order-status-cancelled">
                            <span className="status-dot cancelled"></span>
                            Cancelled
                          </div>
                        ) : (
                          <div className="my-order-status-ontheway">
                            <span className="status-dot ontheway"></span>
                            On the way
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </main>
          </div>
        </div>
      );
    } else {
      // No undelivered orders
      return (
        <div className="order-tracking-container">
          <div className="error-message">
            <h2>No Orders to Track</h2>
            <p>You have no undelivered orders at the moment. All your orders have been delivered or cancelled.</p>
            <div className="order-actions">
              <Link to="/" className="action-button continue-shopping">Continue Shopping</Link>
              <Link to="/account" className="action-button back-to-account">Back to Account</Link>
            </div>
          </div>
        </div>
      );
    }
  }

  if (id && (loading || !order)) {
    return (
      <div className="order-tracking-container">
        <div className="loading-spinner"></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  const statusStep = getStatusStep();

  return (
    <div className="order-tracking-container">
      <h1>Track Your Order</h1>
      {!id && (
        <p className="tracking-subtitle">
          Showing your most recent undelivered order
        </p>
      )}
      
      <div className="order-id">
        <span>Order ID:</span> {order._id}
      </div>
      
      <div className="tracking-status">
        <div className="status-timeline">
          <div className="status-line">
            <div 
              className="status-progress" 
              style={{ width: getProgressWidth() }}
            ></div>
          </div>
          
          <div className="status-steps">
            <div className={`status-step ${statusStep >= 1 ? 'active' : ''}`}>
              <div className="step-icon">1</div>
              <div className="step-label">Processing</div>
              <div className="step-date">{formatDate(order.createdAt)}</div>
            </div>
            
            <div className={`status-step ${statusStep >= 2 && order.orderStatus !== 'Cancelled' ? 'active' : ''}`}>
              <div className="step-icon">2</div>
              <div className="step-label">Shipped</div>
              <div className="step-date">{statusStep >= 2 && order.orderStatus !== 'Cancelled' ? formatDate(order.shippedAt) : 'Pending'}</div>
            </div>
            
            <div className={`status-step ${statusStep >= 3 && order.orderStatus !== 'Cancelled' ? 'active' : ''}`}>
              <div className="step-icon">3</div>
              <div className="step-label">Out for Delivery</div>
              <div className="step-date">{statusStep >= 3 && order.orderStatus !== 'Cancelled' ? formatDate(order.outForDeliveryAt) : 'Pending'}</div>
            </div>
            
            <div className={`status-step ${statusStep >= 4 && order.orderStatus !== 'Cancelled' ? 'active' : ''}`}>
              <div className="step-icon">4</div>
              <div className="step-label">Delivered</div>
              <div className="step-date">{statusStep >= 4 && order.orderStatus !== 'Cancelled' ? formatDate(order.deliveredAt) : 'Pending'}</div>
            </div>
            {order.orderStatus === 'Cancelled' && (
              <div className="status-step cancelled active">
                <div className="step-icon">✖</div>
                <div className="step-label">Cancelled</div>
                <div className="step-date">{formatDate(order.updatedAt)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="order-details-section">
        <div className="order-info">
          <h2>Order Information</h2>
          
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Order Date:</span>
              <span className="info-value">{formatDate(order.createdAt)}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Payment Method:</span>
              <span className="info-value">{order.paymentMethod}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Payment Status:</span>
              <span className={`info-value payment-status ${order.isPaid ? 'paid' : 'unpaid'}`}>
                {order.isPaid ? 'Paid' : 'Unpaid'}
              </span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Delivery Status:</span>
              <span className={`info-value delivery-status status-${order.orderStatus.toLowerCase().replace(' ', '-')}`}>
                {order.orderStatus}
              </span>
            </div>
          </div>
        </div>
        
        <div className="shipping-info">
          <h2>Shipping Address</h2>
          
          <div className="address-details">
            <p className="recipient-name">{order.shippingAddress.fullName}</p>
            <p>{order.shippingAddress.addressLine1}</p>
            {order.shippingAddress.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
            <p>{order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}</p>
            <p>Phone: {order.shippingAddress.phone}</p>
          </div>
        </div>
      </div>
      
      <div className="order-items-section">
        <h2>Order Items</h2>
        
        <div className="order-items-list">
          {order.orderItems.map((item, index) => (
            <div key={index} className="order-item">
              <div className="item-image">
                <img src={item.image} alt={item.title} />
              </div>
              
              <div className="item-details">
                <h3 className="item-title">{item.title}</h3>
                <p className="item-quantity">Quantity: {item.quantity}</p>
                {item.selectedSize ? <p className="item-quantity">Size: {item.selectedSize}</p> : null}
                {/* Dedicated review entry for delivered products */}
                {order.orderStatus === 'Delivered' && !isProductReviewed(item.product) && (
                  <div className="review-entry-card">
                    <div className="review-entry-copy">
                      <h4>Rate & review this product</h4>
                      <p>Open the dedicated review page from here or from your delivery email and submit your feedback in one focused step.</p>
                    </div>
                    <button
                      type="button"
                      className="review-entry-link"
                      onClick={() => navigate(`/review?orderId=${order._id}&productId=${item.product}`)}
                    >
                      Write Review
                    </button>
                  </div>
                )}
                {order.orderStatus === 'Delivered' && isProductReviewed(item.product) && (
                  <div className="review-entry-card review-entry-card--complete">
                    <div className="review-entry-copy">
                      <h4>Review submitted</h4>
                      <p>Your feedback is already saved for this delivered product. Thank you for sharing your experience.</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="item-price">
                ₹{(item.price * item.quantity).toFixed(2)}
              </div>
            </div>
          ))}
        </div>
        
        <div className="order-summary">
          <div className="summary-row">
            <span>Subtotal:</span>
            <span>₹{order.itemsPrice.toFixed(2)}</span>
          </div>
          
          <div className="summary-row">
            <span>Shipping:</span>
            <span>FREE</span>
          </div>
          
          {order.taxPrice > 0 && (
            <div className="summary-row">
              <span>Tax:</span>
              <span>₹{order.taxPrice.toFixed(2)}</span>
            </div>
          )}
          
          <div className="summary-row total">
            <span>Total:</span>
            <span>₹{order.totalPrice.toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      {order.orderStatus === 'Cancelled' && order.cancellationReason && (
        <div className="cancel-reason-box">
          <strong>Cancellation Reason:</strong> {order.cancellationReason}
        </div>
      )}

      {/* Refund Section for Cancelled Paid Orders */}
      {order.orderStatus === 'Cancelled' && order.paymentMethod === 'online' && order.isPaid && (
        <div className="refund-section">
          <h3>Refund Request</h3>
          {refundData ? (
            <div className="refund-status-info">
              <div className="refund-status-badge">
                <span className={`status-${refundData.refundStatus.toLowerCase()}`}>
                  {refundData.refundStatus}
                </span>
              </div>
              <p>Your refund request has been submitted and is being processed.</p>
              {refundData.adminNotes && (
                <div className="admin-notes-preview">
                  <strong>Admin Note:</strong> {refundData.adminNotes}
                </div>
              )}
              <button 
                className="view-refund-details-btn"
                onClick={() => setShowRefundNotification(true)}
              >
                View Refund Details
              </button>
            </div>
          ) : (
            <>
              <p>Your order was paid online and has been cancelled. You can request a refund to your bank account.</p>
              <button 
                className="refund-btn"
                onClick={() => setShowRefundForm(true)}
              >
                Request Refund
              </button>
            </>
          )}
        </div>
      )}

      {refundSuccess && (
        <div className="success-message">
          {refundSuccess}
        </div>
      )}

      {/* Refund Notification Modal */}
      {showRefundNotification && refundData && (
        <RefundNotification
          refund={refundData}
          onClose={() => setShowRefundNotification(false)}
        />
      )}

      {/* Refund Form Modal */}
      {showRefundForm && (
        <RefundForm
          order={order}
          onClose={() => setShowRefundForm(false)}
          onSuccess={(message) => {
            setRefundSuccess(message);
            setShowRefundForm(false);
            setTimeout(() => setRefundSuccess(''), 5000);
            // Refresh refund status after successful submission
            setTimeout(() => checkRefundStatus(order._id), 1000);
          }}
        />
      )}
      
      <div className="order-actions">
        <Link to="/" className="action-button continue-shopping">Continue Shopping</Link>
        <Link to="/account" className="action-button back-to-account">Back to Account</Link>
        <button 
          onClick={() => navigate('/account')} 
          className="action-button view-orders"
        >
          View All Orders
        </button>
        
        {/* Cancel Order Button - Only show for orders that can be cancelled */}
        {order.orderStatus !== 'Cancelled' && 
         order.orderStatus !== 'Delivered' && 
         order.orderStatus !== 'Out for Delivery' && (
          <button 
            onClick={() => setShowCancelModal(true)}
            className="action-button cancel-order"
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px'
            }}
          >
            Cancel Order
          </button>
        )}
      </div>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>Cancel Order</h3>
            
            <p style={{ margin: '0 0 20px 0', color: '#666' }}>
              Are you sure you want to cancel this order? Please provide a reason for cancellation.
            </p>
            
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter cancellation reason..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                marginBottom: '20px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              maxLength={500}
            />
            
            {cancelError && (
              <div style={{ 
                color: '#dc3545', 
                marginBottom: '20px', 
                fontSize: '14px' 
              }}>
                {cancelError}
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                  setCancelError('');
                }}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                disabled={cancelLoading}
              >
                Cancel
              </button>
              
              <button
                onClick={handleCancelOrder}
                disabled={cancelLoading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: cancelLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: cancelLoading ? 0.7 : 1
                }}
              >
                {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingPage;

